/**
 * TripAdvisor Hotel Card for Place Item
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Link from 'client/shared/js/components/Link'
import { Button } from 'reactstrap'
import { MiniPartnerCard } from 'client/app/js/components/MiniCard'
import Logo from '../Logo'
import { COMPANY_NAME, COMPANY_ID } from '../../enums'
import { trackEvent } from 'client/shared/js/utils/tracking'
import styles from './PlaceItemHotelCard.module.scss'

const PlaceItemHotelCard = ({
  className,
  hotel,
  layout,
  size = 'sm',
  bookingText = 'Book',
  offersText = 'See offers',
  disableBooking,
  onClick,
  ...props
}) => {
  const { locationId, name, url, locationString, numReviews, ratingImageUrl, hotelBooking, pricing, photos } = hotel
  const logoSize = size === 'sm' ? '26px' : '32px'
  const pricingOffer = pricing?.offers?.length > 0 ? pricing.offers[0] : {}

  const hotelData = {
    locationId,
    title: name,
    location: locationString,
    bookingUrl: pricingOffer?.clickUrl || hotelBooking?.bookingUrl || url,
    price: pricingOffer?.price,
    photos,
    ratingImageUrl,
    numReviews
  }

  const ActionButton = useMemo(() => {
    const buttonText = hotelData.price ? bookingText : offersText

    return buttonText && (
      <Button
        title={`Book ${hotelData.title} hotel from ${COMPANY_NAME}`}
        color={COMPANY_ID}
        size={size}>
        {buttonText}
      </Button>
    )
  }, [bookingText, offersText, size, hotelData.price, hotelData.title])

  const BookingLink = !disableBooking && (
    <div
      className={cx(styles['logo-hover'])}
      onClick={(e) => {
        // Need this to prevent the drawer from opening when you click the logo
        e.stopPropagation()
        trackEvent('TripAdvisor Booking Clicked', {
          locationId,
          price: pricingOffer?.price || 'See offers'
        })
      }}>
      <Link
        trackProps={{
          'Label': `Book ${COMPANY_NAME} hotel`,
          'Url': hotelData.bookingUrl
        }}
        to={hotelData.bookingUrl}
        anchor
        isNewTab>
        {ActionButton ?? <Logo size='26px' />}
      </Link>
    </div>
  )

  return (
    <MiniPartnerCard
      className={cx(styles['hotel-card'], className, { [styles['clickable']]: onClick })}
      companyId={COMPANY_ID}
      companyName={COMPANY_NAME}
      topHeader={<Logo size={logoSize} />}
      actionButton={BookingLink}
      size={size}
      onClick={onClick}
      {...props}
      {...hotelData} />
  )
}

PlaceItemHotelCard.propTypes = {
  className: PropTypes.string,
  hotel: PropTypes.object.isRequired,
  layout: PropTypes.oneOf(['card', 'list']),
  size: PropTypes.oneOf(['sm', 'lg']),
  bookingText: PropTypes.string,
  offersText: PropTypes.string,
  disableBooking: PropTypes.bool,
  onClick: PropTypes.func
}

export default PlaceItemHotelCard
